import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Users2, Search, X, Save, Flag, Trophy, BadgeCheck, Hash, Phone, Building2, ChevronRight,
} from "lucide-react";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_admin_token") || ""}`,
});

const ROLE_LABEL = {
  null: "Jugador", undefined: "Jugador", "": "Jugador",
  club_master: "Club Master", club_capturista: "Capturista", club_operador: "Operador",
};

const fmtToPar = (v) => (v == null ? "—" : v === 0 ? "E" : v > 0 ? `+${v}` : `${v}`);

const SOURCE = {
  casual: { label: "Casual", cls: "bg-emerald-100 text-emerald-700" },
  foursome: { label: "Foursome", cls: "bg-sky-100 text-sky-700" },
  tournament: { label: "Torneo", cls: "bg-amber-100 text-amber-700" },
};

export default function AdminPlayers() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // user_id
  const [detail, setDetail] = useState(null);
  const [rounds, setRounds] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchPlayers = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/players?q=${encodeURIComponent(q)}`, {
        credentials: "include", headers: authHeaders(),
      });
      if (res.ok) setPlayers(await res.json());
    } catch {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

  useEffect(() => {
    const t = setTimeout(() => fetchPlayers(query), 350);
    return () => clearTimeout(t);
  }, [query, fetchPlayers]);

  const openDetail = async (uid) => {
    setSelected(uid);
    setDetail(null);
    setRounds(null);
    try {
      const [dRes, rRes] = await Promise.all([
        fetch(`${API}/admin/players/${uid}`, { credentials: "include", headers: authHeaders() }),
        fetch(`${API}/admin/players/${uid}/rounds`, { credentials: "include", headers: authHeaders() }),
      ]);
      const d = await dRes.json();
      setDetail(d);
      setForm({
        name: d.profile.name || "",
        phone: d.profile.phone || "",
        club: d.profile.club || "",
        handicap_index: d.profile.handicap_index ?? "",
        ghin_number: d.profile.ghin_number || "",
        status: d.profile.status || "active",
      });
      if (rRes.ok) setRounds(await rRes.json());
    } catch {
      toast.error("Error al cargar el usuario");
    }
  };

  const closeDetail = () => { setSelected(null); setDetail(null); setRounds(null); setForm(null); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/players/${selected}`, {
        method: "PUT", credentials: "include", headers: authHeaders(),
        body: JSON.stringify({
          name: form.name, phone: form.phone, club: form.club,
          handicap_index: form.handicap_index === "" ? null : form.handicap_index,
          ghin_number: form.ghin_number, status: form.status,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Error"); }
      const updated = await res.json();
      toast.success("Usuario actualizado");
      setDetail((d) => ({ ...d, profile: updated }));
      setPlayers((list) => list.map((p) => (p.user_id === selected ? updated : p)));
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="admin-players-title">
          <Users2 className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
          Usuarios
        </h1>
        <p className="text-sm text-[#64748B] mt-1">Gestiona golfistas: índice (HCP), GHIN, rondas y más</p>
      </div>

      <div className="relative mb-5">
        <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, email, GHIN o teléfono"
          className="pl-9"
          data-testid="admin-players-search"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : players.length === 0 ? (
        <Card className="card-default"><CardContent className="py-12 text-center text-[#64748B]">
          Sin usuarios para esta búsqueda
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="admin-players-list">
          {players.map((p) => (
            <button
              key={p.user_id}
              onClick={() => openDetail(p.user_id)}
              className="w-full text-left bg-white rounded-xl border border-gray-100 hover:border-[#1B4D3E]/30 hover:shadow-sm transition p-4 flex items-center gap-3"
              data-testid={`admin-player-${p.user_id}`}
            >
              <div className="w-10 h-10 rounded-full bg-[#1B4D3E] flex items-center justify-center text-white font-bold text-sm shrink-0">
                {p.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#0F172A] truncate">{p.name || "—"}</p>
                <p className="text-xs text-[#64748B] truncate">{p.email}</p>
              </div>
              <div className="hidden sm:flex items-center gap-4 text-sm">
                <div className="text-center w-14">
                  <p className="text-[10px] text-[#94A3B8] uppercase">HCP</p>
                  <p className="font-bold text-[#0F172A]">{p.handicap_index ?? "—"}</p>
                </div>
                <div className="text-center w-20">
                  <p className="text-[10px] text-[#94A3B8] uppercase">GHIN</p>
                  <p className="font-medium text-[#334155] truncate">{p.ghin_number || "—"}</p>
                </div>
                <span className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
                  {ROLE_LABEL[p.role] || p.role}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-[#CBD5E1] shrink-0" />
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-player-detail">
            {!detail ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-[#1B4D3E] flex items-center justify-center text-white font-bold">
                      {detail.profile.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-[#0F172A] truncate">{detail.profile.name}</p>
                      <p className="text-xs text-[#64748B] truncate">{detail.profile.email}</p>
                    </div>
                  </div>
                  <button onClick={closeDetail} data-testid="admin-player-close"><X className="w-5 h-5 text-[#64748B]" /></button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Edit form */}
                  <div>
                    <h3 className="text-sm font-bold text-[#0F172A] mb-3 flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#1B4D3E]" /> Perfil</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5 col-span-2 sm:col-span-1">
                        <Label className="text-xs">Nombre</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="player-form-name" />
                      </div>
                      <div className="space-y-1.5 col-span-2 sm:col-span-1">
                        <Label className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Teléfono</Label>
                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="player-form-phone" />
                      </div>
                      <div className="space-y-1.5 col-span-2 sm:col-span-1">
                        <Label className="text-xs flex items-center gap-1"><Flag className="w-3 h-3" /> Índice de hándicap (HCP)</Label>
                        <Input value={form.handicap_index} onChange={(e) => setForm({ ...form, handicap_index: e.target.value })} placeholder="ej. 12.5 o +1.2" data-testid="player-form-hcp" />
                      </div>
                      <div className="space-y-1.5 col-span-2 sm:col-span-1">
                        <Label className="text-xs flex items-center gap-1"><Hash className="w-3 h-3" /> Número GHIN</Label>
                        <Input value={form.ghin_number} onChange={(e) => setForm({ ...form, ghin_number: e.target.value })} placeholder="ej. 1234567" data-testid="player-form-ghin" />
                      </div>
                      <div className="space-y-1.5 col-span-2 sm:col-span-1">
                        <Label className="text-xs flex items-center gap-1"><Building2 className="w-3 h-3" /> Club</Label>
                        <Input value={form.club} onChange={(e) => setForm({ ...form, club: e.target.value })} data-testid="player-form-club" />
                      </div>
                      <div className="space-y-1.5 col-span-2 sm:col-span-1">
                        <Label className="text-xs">Estado</Label>
                        <select
                          value={form.status}
                          onChange={(e) => setForm({ ...form, status: e.target.value })}
                          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                          data-testid="player-form-status"
                        >
                          <option value="active">Activo</option>
                          <option value="inactive">Inactivo</option>
                        </select>
                      </div>
                    </div>
                    <Button onClick={save} disabled={saving} className="mt-4 bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="player-save-btn">
                      <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando…" : "Guardar cambios"}
                    </Button>
                  </div>

                  {/* Memberships + tournaments */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-bold text-[#0F172A] mb-2 flex items-center gap-2"><Building2 className="w-4 h-4 text-[#1B4D3E]" /> Membresías</h3>
                      {detail.memberships.length === 0 ? (
                        <p className="text-xs text-[#94A3B8]">Sin membresías</p>
                      ) : (
                        <div className="space-y-1.5">
                          {detail.memberships.map((m, i) => (
                            <div key={i} className="text-sm bg-gray-50 rounded-lg px-3 py-2">
                              <p className="font-medium text-[#0F172A]">{m.club_name || m.club_id}</p>
                              {m.category && <p className="text-xs text-[#64748B]">{m.category}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-[#0F172A] mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#1B4D3E]" /> Torneos ({detail.tournaments.length})</h3>
                      {detail.tournaments.length === 0 ? (
                        <p className="text-xs text-[#94A3B8]">Sin torneos</p>
                      ) : (
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {detail.tournaments.map((t) => (
                            <div key={t.tournament_id} className="text-sm bg-gray-50 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                              <span className="truncate text-[#0F172A]">{t.name}</span>
                              <span className="text-[10px] text-[#64748B] shrink-0">{t.start_date || ""}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rounds */}
                  <div>
                    <h3 className="text-sm font-bold text-[#0F172A] mb-2 flex items-center gap-2"><Flag className="w-4 h-4 text-[#1B4D3E]" /> Rondas {rounds ? `(${rounds.rounds.length})` : ""}</h3>
                    {rounds?.stats && (
                      <div className="flex gap-4 mb-3 bg-[#0B1D33] rounded-xl px-4 py-3 text-white">
                        <div><p className="text-[10px] uppercase text-white/50">Prom.</p><p className="font-extrabold text-lg">{rounds.stats.avg_score}</p></div>
                        <div><p className="text-[10px] uppercase text-white/50">vs Par</p><p className="font-extrabold text-lg">{fmtToPar(Math.round(rounds.stats.avg_to_par))}</p></div>
                        <div className="ml-auto text-right"><p className="text-[10px] uppercase text-white/50">Rondas</p><p className="font-extrabold text-lg">{rounds.stats.rounds_counted}</p></div>
                      </div>
                    )}
                    {!rounds ? (
                      <p className="text-xs text-[#94A3B8]">Cargando rondas…</p>
                    ) : rounds.rounds.length === 0 ? (
                      <p className="text-xs text-[#94A3B8]">Sin rondas registradas</p>
                    ) : (
                      <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="player-rounds-list">
                        {rounds.rounds.slice(0, 40).map((r) => {
                          const s = SOURCE[r.source];
                          return (
                            <div key={r.round_key} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${s.cls}`}>{s.label}</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#0F172A] truncate">
                                  {r.round_label ? `${r.round_label} · ` : ""}{r.title || "Ronda"}
                                </p>
                                <p className="text-xs text-[#64748B] truncate">{[r.course, r.date].filter(Boolean).join(" · ")}</p>
                              </div>
                              {r.gross ? (
                                <div className="text-right shrink-0">
                                  <span className="font-bold text-[#0F172A]">{r.gross}</span>
                                  {r.to_par != null && <span className="text-xs text-[#1B4D3E] ml-1">{fmtToPar(r.to_par)}</span>}
                                </div>
                              ) : <span className="text-xs text-[#94A3B8]">{r.status === "in_progress" ? "En juego" : "—"}</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
